// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.

import { computeGroupDimensions, layoutChildren, updateSiblings } from '../layoutCore/layoutCore';
import { getAncestorChain } from '../toggle/toggle';

/* ============================================================
   ================= NODE LAYOUT RECALCULATION ================
   ============================================================ */

export const updateParentLayout = (nodes, nodeId) => {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const currentNode = nodeMap.get(nodeId);
  if (!currentNode?.parentId) {
    return nodes;
  }

  const parentId = currentNode.parentId;

  const { height: updatedParentHeight, width: updatedParentWidth } = computeGroupDimensions(
    nodes,
    parentId
  );

  let updatedNodes = nodes.map(n =>
    n.id === parentId
      ? { ...n, data: { ...n.data, height: updatedParentHeight, width: updatedParentWidth } }
      : n
  );

  updatedNodes = layoutChildren(updatedNodes, parentId);

  return updatedNodes;
};

export const relayoutGroup = (nodes, groupId) => {
  const group = nodes.find(n => n.id === groupId);
  if (!group?.data?.isOpen) {
    return nodes;
  }

  // Compute dimensions first so layoutChildren can use the updated width for centering
  const { height: newHeight, width: newWidth } = computeGroupDimensions(nodes, groupId);

  let updated = nodes.map(n =>
    n.id === groupId ? { ...n, data: { ...n.data, height: newHeight, width: newWidth } } : n
  );

  updated = layoutChildren(updated, groupId);

  // Propagate size changes up the full ancestor chain (direct parent first, root last)
  const ancestors = getAncestorChain(groupId, updated).reverse();
  ancestors.forEach(ancestorId => {
    const ancestor = updated.find(n => n.id === ancestorId);
    if (!ancestor || !ancestor.data?.isOpen) {
      return;
    }

    const { height: aHeight, width: aWidth } = computeGroupDimensions(updated, ancestorId);

    updated = updated.map(n =>
      n.id === ancestorId ? { ...n, data: { ...n.data, height: aHeight, width: aWidth } } : n
    );

    updated = layoutChildren(updated, ancestorId);
    updated = updateSiblings(updated, ancestorId);
  });

  return updated;
};
